/**
 * PURPOSE: Provide reducers that are required for the initial boostrap of the application.  These reducers cannot
 *   be selectively loading based on feature toggle flags.  For the the other reducers that can be conditionally
 *   added take a look at `remainingReducers.ts`.
 */

// libraries
import { navigationReducer } from '@makemydeal/dr-activities-common';
import { locHrefUtil, apiConfigHelper, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import type { IConfigSalesCheckin } from '@makemydeal/dr-sales-checkin-types';

// reducers
import { configReducer, sharedBootstrapReducer, featureToggleReducer } from '@makemydeal/dr-shared-store';
import { DEFAULT_PROTOCOL, SALES_CHECKIN_DEV2_DOMAIN, STATIC_IMAGES_BASE_PATH } from '@makemydeal/dr-sales-checkin-ui-utils';
import { sessionExpiredReducer } from '@makemydeal/dr-sales-checkin-store';

export const initialState: IConfigSalesCheckin = {
    services: {
        bff: {
            host: '',
            protocol: ''
        }
    },
    staticImages: {
        host: '',
        protocol: DEFAULT_PROTOCOL
    }
};

/**
 * Returns the static assets config.
 * If running locally, uses 'dev2' prefix for host.
 */
function getStaticAssetsConfig(currentHost: string, protocol: string = DEFAULT_PROTOCOL): IConfigSalesCheckin['staticImages'] {
    const isLocal = currentHost.includes('localhost');
    const host = isLocal ? SALES_CHECKIN_DEV2_DOMAIN : currentHost;
    return {
        host,
        base: STATIC_IMAGES_BASE_PATH,
        protocol
    };
}

const handleBootstrapInitialLoad: configReducer.BuildConfigReducerBootstrapInitialLoadCallback<IConfigSalesCheckin> = (
    appPrefix,
    action,
    state
) => {
    // get urls based on windows location
    const bffApiConfig = apiConfigHelper.buildBffApiConfig(appPrefix, locHrefUtil.getLocHref());
    const shallowCloned = { ...state };
    shallowCloned.services = {
        ...state.services,
        bff: {
            ...state.services.bff,
            ...bffApiConfig.configObj
        }
    };
    shallowCloned.staticImages = getStaticAssetsConfig(bffApiConfig.configObj.host);
    return shallowCloned;
};

export const initialReducers = {
    sharedBootstrap: sharedBootstrapReducer.reducer,
    config: configReducer.buildConfigReducer(SALES_CHECKIN_APP_PREFIX, initialState, handleBootstrapInitialLoad),
    featureToggles: featureToggleReducer.reducer,
    navigation: navigationReducer,
    sessionExpiry: sessionExpiredReducer
};
